import { useRef, useEffect, useState } from 'react';
import {
  getActiveState,
  isDuplicateMsg,
  parseJSON,
} from '../helpers/titreHelper';
import { useGetParams } from './useGetParams';
import { useInterval } from './useInterval';

export const useBroadcast = () => {
  const { user } = useGetParams();

  const lastMessageIdRef = useRef(0);
  const [currentState, setCurrentState] = useState(null);
  const [isVisible, setIsVisible] = useState(null);

  const [allMessages, setAllMessages] = useState([]);

  const protocol = window?.location.protocol || 'https:';

  // id предыдущего запущенного интерактива
  const prevIdBroadcast = useRef('');
  // id текущего запущенного интерактива
  const iactiveIdRef = useRef('');

  const getBroadcastInfo = async () => {
    try {
      if (!user) {
        return;
      }

      const response = await fetch(
        `${protocol}//api.stream.iactive.pro/titreInfo?user=${user}&from=${lastMessageIdRef.current}&type=1`,
        { cache: 'no-store' },
      );
      const data = await response.json();

      if (!data) return;

      let { messagesList, deletedMessagesIdList, visibility, iactiveId } = data;

      // очистка при остановке\смене интерактива, при остановке iactiveId = ""
      if (iactiveIdRef.current !== iactiveId) {
        if (
          iactiveId &&
          iactiveId !== prevIdBroadcast.current // если === , значит prevIdBroadcast был остановлен и теперь запущен, и не нужно обнулять
        ) {
          setAllMessages([]);
        }
      }

      // сохранение id нового запущенного интерактива.
      if (iactiveId && iactiveId !== prevIdBroadcast.current) {
        // Если уже был предыдущий запущенный интерактив, то показать сообщение сразу без задержки.
        prevIdBroadcast.current = iactiveId;
      }

      // Определяем видимость титра
      if (!visibility && visibility !== isVisible) {
        setIsVisible(false);
        return;
      } else if (visibility && visibility !== isVisible) {
        setIsVisible(true);
      }

      // Определяем состояние (S1, S2 ...)
      if (
        data?.statusList[0]?.state &&
        getActiveState(data?.statusList[0]?.state) !== currentState
      ) {
        setCurrentState(getActiveState(data?.statusList[0].state));
      }

      // если список сообщений пустой
      if (
        !messagesList ||
        (Array.isArray(messagesList) && messagesList.length === 0)
      ) {
        // если имеются в списке сообщений удаленные
        if (
          allMessages.some((el) =>
            deletedMessagesIdList.includes(Number(el.id)),
          )
        ) {
          // удаление сообщений
          let messagesNoDelete = [...allMessages].filter(
            (el) => !deletedMessagesIdList.includes(Number(el.id)),
          );
          setAllMessages(messagesNoDelete);
        }

        //запоминаем id последнего сообщения, для дальнейших запросов и обнуляем при переключении интерактивов
        if (iactiveIdRef.current && iactiveIdRef.current !== iactiveId) {
          lastMessageIdRef.current = 0;
          iactiveIdRef.current = iactiveId;
          return;
        } else {
          iactiveIdRef.current = iactiveId;
          lastMessageIdRef.current =
            messagesList[0]?.id || lastMessageIdRef.current;
        }
        return;
      }

      //запоминаем id последнего сообщения, для дальнейших запросов и обнуляем при переключении интерактивов
      if (iactiveIdRef.current && iactiveIdRef.current !== iactiveId) {
        lastMessageIdRef.current = 0;
        iactiveIdRef.current = iactiveId;
        return;
      } else {
        iactiveIdRef.current = iactiveId;
        lastMessageIdRef.current =
          messagesList[0]?.id || lastMessageIdRef.current;
      }

      messagesList = messagesList.map((m) => ({
        ...m,
        message: {
          ...m.message,
          attachments: parseJSON(m.message?.attachments),
        },
      }));

      // фильтруем сообщения только с вложениями, и НЕ аудио
      messagesList = messagesList.filter(
        (el) =>
          Array.isArray(el.message.attachments) &&
          el.message.attachments.length > 0 &&
          typeof el.message.attachments[0] === 'object' &&
          el.message.attachments[0].type !== 'audio_msg',
      );

      // без дубликатов
      let noDuplicateMsg = [];
      messagesList.forEach((msg) => {
        const isDuplicate = isDuplicateMsg(msg, allMessages);

        if (!isDuplicate) {
          noDuplicateMsg.push(msg);
        }
      });

      // предзагрузка медиа
      noDuplicateMsg.forEach((el, i) => {
        let { attachments } = el.message;
        if (
          typeof attachments === 'string' &&
          attachments.replace(/&quot;/g, '"') !== ''
        ) {
          attachments = JSON.parse(attachments.replace(/&quot;/g, '"'));
        }
      });

      // сохранение сообщений
      if (noDuplicateMsg.length) {
        setAllMessages((prev) => {
          return [...prev, ...noDuplicateMsg];
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getBroadcastInfo();
  }, []);

  // Получение информации о титре
  useInterval(getBroadcastInfo, 1000);

  return {
    currentState,
    isVisible,
    allMessages,
  };
};
